/**
 * Google Analytics Utility
 *
 * Provides helper functions for tracking events and page views.
 * Uses the gtag API loaded in index.html.
 */

// GA Measurement ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-QT04SVWBRC';

/**
 * Check if gtag is available
 */
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view
 * @param {string} path - The page path (e.g., '/app', '/pricing')
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!isGtagAvailable()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'sign_up', 'purchase')
 * @param {object} params - Additional event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', eventName, params);
};

// ============================================
// Core Web Vitals Tracking
// ============================================

/**
 * Track a Web Vital metric
 * @param {Object} metric - The web vital metric object from web-vitals library
 * @param {string} metric.name - Metric name (CLS, FID, FCP, LCP, TTFB, INP)
 * @param {number} metric.value - Metric value
 * @param {string} metric.rating - Rating (good, needs-improvement, poor)
 * @param {number} metric.delta - Delta since last report
 * @param {string} metric.id - Unique metric ID
 * @param {string} metric.navigationType - Navigation type (navigate, reload, back_forward, prerender)
 */
export const trackWebVital = (metric) => {
  if (!isGtagAvailable()) return;

  // Send to Google Analytics as a custom event
  window.gtag('event', metric.name, {
    event_category: 'Web Vitals',
    event_label: metric.id,
    // Google Analytics expects integers, so round to nearest integer
    // For CLS, multiply by 1000 for better precision (it's typically 0.xxx)
    value: metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value),
    // Custom dimensions
    metric_rating: metric.rating,
    metric_delta: Math.round(metric.delta),
    navigation_type: metric.navigationType,
    // Non-interaction event - doesn't affect bounce rate
    non_interaction: true,
  });

  // Also log to console in development for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vital] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      navigationType: metric.navigationType,
    });
  }
};

/**
 * Initialize Core Web Vitals tracking
 * Call this once when the app loads (e.g., in index.js or App.js)
 * Requires the 'web-vitals' package to be installed
 * 
 * @example
 * // In your App.js or index.js:
 * import { initWebVitals } from './lib/analytics';
 * initWebVitals();
 */
export const initWebVitals = async () => {
  try {
    // Dynamic import to avoid bundling if not used
    const { onCLS, onFID, onFCP, onLCP, onTTFB, onINP } = await import('web-vitals');

    // Largest Contentful Paint - measures loading performance
    // Good: < 2.5s, Needs Improvement: 2.5s - 4s, Poor: > 4s
    onLCP(trackWebVital);

    // First Input Delay - measures interactivity
    // Good: < 100ms, Needs Improvement: 100ms - 300ms, Poor: > 300ms
    onFID(trackWebVital);

    // Cumulative Layout Shift - measures visual stability
    // Good: < 0.1, Needs Improvement: 0.1 - 0.25, Poor: > 0.25
    onCLS(trackWebVital);

    // First Contentful Paint - measures when content first appears
    // Good: < 1.8s, Needs Improvement: 1.8s - 3s, Poor: > 3s
    onFCP(trackWebVital);

    // Time to First Byte - measures server response time
    // Good: < 800ms, Needs Improvement: 800ms - 1.8s, Poor: > 1.8s
    onTTFB(trackWebVital);

    // Interaction to Next Paint - measures responsiveness
    // Good: < 200ms, Needs Improvement: 200ms - 500ms, Poor: > 500ms
    onINP(trackWebVital);

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Core Web Vitals tracking initialized');
    }
  } catch (error) {
    // web-vitals package might not be installed, fail silently
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] Could not initialize Web Vitals:', error.message);
      console.warn('Install web-vitals package: npm install web-vitals');
    }
  }
};

// ============================================
// Pre-defined Event Trackers
// ============================================

/**
 * Track user sign up
 * @param {string} method - Sign up method (e.g., 'email', 'google')
 */
export const trackSignUp = (method = 'auth0') => {
  trackEvent('sign_up', { method });
};

/**
 * Track user login
 * @param {string} method - Login method
 */
export const trackLogin = (method = 'auth0') => {
  trackEvent('login', { method });
};

/**
 * Track subscription purchase
 * @param {string} tier - Subscription tier (essentials, excellence, mastery, ultimate)
 * @param {string} billingPeriod - Billing period (monthly, 3-month, 6-month)
 * @param {number} value - Price value
 */
export const trackPurchase = (tier, billingPeriod, value) => {
  trackEvent('purchase', {
    currency: 'USD',
    value,
    items: [{
      item_id: `${tier}_${billingPeriod}`,
      item_name: `${tier} - ${billingPeriod}`,
      item_category: 'subscription',
      price: value,
    }],
  });
};

/**
 * Track exam started
 * @param {string} examType - Type of exam (practice, adaptive, full)
 * @param {string} examId - Exam identifier
 */
export const trackExamStart = (examType, examId) => {
  trackEvent('exam_start', {
    exam_type: examType,
    exam_id: examId,
  });
};

/**
 * Track exam completed
 * @param {string} examType - Type of exam
 * @param {string} examId - Exam identifier
 * @param {number} score - Score percentage (0-100)
 * @param {number} duration - Duration in seconds
 */
export const trackExamComplete = (examType, examId, score, duration) => {
  trackEvent('exam_complete', {
    exam_type: examType,
    exam_id: examId,
    score,
    duration,
  });
};

/**
 * Track flashcard session
 * @param {string} subject - Flashcard subject
 * @param {number} cardsReviewed - Number of cards reviewed
 * @param {number} duration - Duration in seconds
 */
export const trackFlashcardSession = (subject, cardsReviewed, duration) => {
  trackEvent('flashcard_session', {
    subject,
    cards_reviewed: cardsReviewed,
    duration,
  });
};

/**
 * Track quiz completed
 * @param {string} quizId - Quiz identifier
 * @param {number} score - Score percentage
 */
export const trackQuizComplete = (quizId, score) => {
  trackEvent('quiz_complete', {
    quiz_id: quizId,
    score,
  });
};

/**
 * Track feature usage
 * @param {string} feature - Feature name (e.g., 'study_plan', 'calendar', 'lectures')
 */
export const trackFeatureUse = (feature) => {
  trackEvent('feature_use', {
    feature_name: feature,
  });
};

/**
 * Track button click
 * @param {string} buttonName - Button identifier
 * @param {string} location - Where the button is located
 */
export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', {
    button_name: buttonName,
    location,
  });
};

/**
 * Track errors
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} location - Where the error occurred
 */
export const trackError = (errorType, errorMessage, location) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    location,
  });
};

/**
 * Track successful error recovery (HP-002)
 * Called after app successfully loads following an error recovery attempt.
 * @param {string} errorType - The type of error that was recovered from
 * @param {string} recoveryMethod - Method used (reload, clear_cache, go_home, auto_redirect)
 */
export const trackRecoverySuccess = (errorType, recoveryMethod) => {
  trackEvent('error_recovery_success', {
    error_type: errorType,
    recovery_method: recoveryMethod,
  });
};

/**
 * Check for pending recovery and report success (HP-002)
 * Call this on app mount to track successful error recoveries.
 * Recovery is considered successful if app loads within 60 seconds of recovery attempt.
 */
export const checkAndReportRecoverySuccess = () => {
  try {
    const pending = sessionStorage.getItem('error_recovery_pending');
    if (pending) {
      const { errorType, method, timestamp } = JSON.parse(pending);
      // Only count if within last 60 seconds
      if (Date.now() - timestamp < 60000) {
        trackRecoverySuccess(errorType, method);
      }
      sessionStorage.removeItem('error_recovery_pending');
    }
  } catch (e) {
    // Ignore errors - recovery check is non-critical
  }
};

export default {
  trackPageView,
  trackEvent,
  trackWebVital,
  initWebVitals,
  trackSignUp,
  trackLogin,
  trackPurchase,
  trackExamStart,
  trackExamComplete,
  trackFlashcardSession,
  trackQuizComplete,
  trackFeatureUse,
  trackButtonClick,
  trackError,
  trackRecoverySuccess,
  checkAndReportRecoverySuccess,
};
